import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '../fetchBaseQuery';
import type {
    DeleteScreenshotsRequest,
    GetTagsRequest,
    GetTagsResponse,
    GetTagsResponseRaw,
    TagsRequest,
} from './types';

const screenshotsApi = createApi({
    reducerPath: 'screenshots',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['screenshot-tags'],
    endpoints: (builder) => ({
        getTags: builder.query<GetTagsResponse, GetTagsRequest>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/screenshots/${projectId}/list-tags`,
            }),
            transformResponse: ({ tags }: GetTagsResponseRaw) => tags,
            providesTags: ['screenshot-tags'],
        }),
        addTagsToScreenshots: builder.mutation<void, TagsRequest>({
            query: ({ projectId, screenshotIds, tags }) => {
                const body = new URLSearchParams();
                body.append('project_id', projectId);
                body.append('action', 'add-tags');
                screenshotIds.forEach((entry) => body.append('screenshot_ids[]', `${entry}`));
                tags.forEach((entry) => body.append('tags[]', entry));

                return {
                    method: 'POST',
                    url: '/screenshots/mass-action',
                    body,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
            invalidatesTags: ['screenshot-tags'],
        }),
        removeTagsFromScreenshots: builder.mutation<void, TagsRequest>({
            query: ({ projectId, screenshotIds, tags }) => {
                const body = new URLSearchParams();
                body.append('project_id', projectId);
                body.append('action', 'remove-tags');
                screenshotIds.forEach((entry) => body.append('screenshot_ids[]', `${entry}`));
                tags.forEach((entry) => body.append('tags[]', entry));

                return {
                    method: 'POST',
                    url: '/screenshots/mass-action',
                    body,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
            invalidatesTags: ['screenshot-tags'],
        }),
        deleteScreenshots: builder.mutation<void, DeleteScreenshotsRequest>({
            query: ({ projectId, screenshotIds }) => {
                const body = new URLSearchParams();
                body.append('project_id', projectId);
                body.append('action', 'delete-screenshots');
                screenshotIds.forEach((entry) => body.append('screenshot_ids[]', `${entry}`));

                return {
                    url: '/screenshots/mass-action',
                    method: 'POST',
                    body,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
    }),
});

export default screenshotsApi;

export const {
    useGetTagsQuery,
    useDeleteScreenshotsMutation,
    useAddTagsToScreenshotsMutation,
    useRemoveTagsFromScreenshotsMutation,
} = screenshotsApi;
